import React, { useEffect, useState } from "react";
import { Box, Button, styled } from "@mui/material";
import { fontSizes } from "../../../theme/font-sizes";
import { darkColors } from "../../../theme/colors";
import { assetIcons } from "../../../constants/assets";
import { formatTime } from "../format/time";
import { SparkContainer } from "./find-match-button";
import InlineSvg from "../inline-svg";

const StyledButton = styled(Button)(({ backgroundImage }) => ({
  fontSize: `clamp(24px, 2vw,${fontSizes.superHeading4})`,
  fontFamily: "MontserratBold",
  borderRadius: "12px", // Rounded corners
  textTransform: "none",
  position: "relative", // Relative to enable absolute positioning of text
  overflow: "hidden",
  color: "white",
  zIndex: 2,
  background: "linear-gradient(135deg, #6C1717 100%, #EE8005)", // Adjusted gradient
  boxShadow: "-2px 0px 14px rgba(199, 183, 40, 0.27)", // Shadow
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -2,
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: "right",
  },

  "&:hover": {
    background: "linear-gradient(135deg, #FD8804, #8E5009)",
    boxShadow: "-2px 5px 32px rgba(199, 183, 40, 0.47)",
  },
}));
const ContentContainer = styled("div")({
  position: "absolute",
  right: "24px",
  display: "flex",
  flexDirection: "column", // Stack items vertically
  color: "white",
  lineHeight: 1.2,
});

const Timer = styled("span")({
  fontSize: fontSizes.superHeading2,
  textAlign: "right",
  fontFamily: "MontserratBold",
  color: darkColors.findMatchBlack,
  fontStyle: "italic",
});

const Status = styled("span")({
  fontSize: fontSizes.heading3, // Slightly smaller than timer
  fontFamily: "MontserratBold",
  display: "flex",
  alignItems: "center",
});
const GalaxyContainer = styled("div")({
  position: "absolute",
  inset: 0,
  opacity: 0,
  transition: "opacity 0.25s",
  mask: "radial-gradient(white, transparent)",
});

const Galaxy = styled("div")({
  position: "absolute",
  width: "100%",
  aspectRatio: "1",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  overflow: "hidden",
  opacity: 0,
  transition: "opacity 0.3s",
});

const GalaxyRing = styled("div")({
  height: "200%",
  width: "200%",
  position: "absolute",
  top: "50%",
  left: "50%",
  borderRadius: "50%",
  transform:
    "translate(-28%, -40%) rotateX(-24deg) rotateY(-30deg) rotateX(90deg)",
  transformStyle: "preserve-3d",
});

const Star = styled("span")({
  position: "absolute",
  backgroundColor: "white",
  borderRadius: "50%",
  opacity: "var(--alpha, 0.7)",
  top: "50%",
  left: "30%",
  width: "calc(var(--size, 4) * 1px)",
  height: "calc(var(--size, 4) * 1px)",
  transform:
    "translate(-50%, -50%) rotate(10deg) rotate(0deg) translateY(calc(var(--distance, 50) * 1px))",
  animation:
    "orbit calc(var(--duration, 10) * 1s) calc(var(--delay, 0) * -1s) infinite linear",
  "@keyframes orbit": {
    to: {
      transform:
        "translate(-50%, -50%) rotate(10deg) rotate(360deg) translateY(calc(var(--distance, 50) * 1px))",
    },
  },
});

const FindingMatchButton = ({
  text,
  py = 2,
  width = "auto",
  backgroundImage,
  onClick,
  seconds,
  ...props
}) => {
  return (
    <StyledButton
      variant="contained"
      onClick={onClick}
      sx={{
        py: py,
        width: width,
        "&:hover .galaxy-container, &:hover .galaxy": {
          opacity: 1,
        },
        "&:hover .spark-container": {
          opacity: 1,
        },
      }}
      backgroundImage={backgroundImage}
      {...props}
    >
      <SparkContainer className="spark-container">
        <div className="spark-top" />
        <div className="spark-bottom" />
      </SparkContainer>
      <ContentContainer>
        <Timer>{formatTime(seconds)}</Timer>
        <Status>
          <InlineSvg sx={{ mr: 1 }} url={assetIcons.cancelIcon} />
          FINDING...
        </Status>
      </ContentContainer>
    </StyledButton>
  );
};

export default FindingMatchButton;
